import { Content, Header, Page, Spinner } from '@vivicta/ui';
import type { ReactNode } from 'react';

import { ConnectGitHubPrompt } from './ConnectGitHubPrompt';
import {
  GitHubConnectionContext,
  useGitHubConnection,
} from './useGitHubConnection';

/**
 * For pages that read or act on GitHub as the user (ADR-0014 tier 2): until
 * the user has connected their company GitHub account, render `preview` (what
 * the page can show without the user's token, with a `GitHubConnectionBanner`)
 * or, without one, only the page header and the Connect prompt.
 */
export const GitHubConnectionGate = ({
  title,
  subtitle,
  reason,
  preview,
  children,
}: {
  /** The page's own header, so the page looks the same before and after connecting. */
  title: string;
  subtitle?: string;
  /** What the page does with GitHub, e.g. "Onboarding creates repositories in vivicta-sandbox". */
  reason: ReactNode;
  /** Rendered instead of the prompt while GitHub is not connected. */
  preview?: ReactNode;
  children: ReactNode;
}) => {
  const connection = useGitHubConnection();

  if (connection.state === 'connected') {
    return <>{children}</>;
  }

  if (connection.state === 'none' && preview) {
    return (
      <GitHubConnectionContext.Provider value={connection}>
        {preview}
      </GitHubConnectionContext.Provider>
    );
  }

  return (
    <Page>
      <Header title={title} subtitle={subtitle} />
      <Content>
        {connection.state === 'checking' ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : (
          <ConnectGitHubPrompt connection={connection} reason={reason} />
        )}
      </Content>
    </Page>
  );
};
